import React, { useState } from 'react'
import { AllocationStatusItem, AllocationStatusPageMetaProps } from '../data/types'
import { CheckIcon, XIcon } from '../icons'
import { Button } from './ui'
import { EyeIcon } from '../icons'

type Props = {
    meta: AllocationStatusPageMetaProps
    allocations: AllocationStatusItem[]
    selectedIds: number[]
    onSelectAll: (checked: boolean) => void
    onSelectOne: (id: number, checked: boolean) => void
    onReAllocate: (id: number) => void
    onProfileClick?: (allocation: AllocationStatusItem) => void
    onReviewClick?: (allocationIds: number[], status: boolean) => Promise<void>
    canReallocate?: boolean
}

export const AllocationStatusTable = (props: Props) => {
    const {
        meta,
        allocations,
        selectedIds,
        onSelectAll,
        onSelectOne,
        onReAllocate,
        onProfileClick,
        onReviewClick,
        canReallocate = true
    } = props

    const allSelected = allocations.length > 0 && allocations.every(a => selectedIds.includes(a.id))
    const someSelected = selectedIds.length > 0 && !allSelected

    const getStatusBadge = (status: boolean | null) => {
        switch (status) {
            case true:
                return (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-success-100 text-success-800 dark:bg-success-900 dark:text-success-200">
                        <CheckIcon className="w-3 h-3 me-1" />
                        {meta.acceptedLabel}
                    </span>
                )
            case false:
                return (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-danger-100 text-danger-800 dark:bg-danger-900 dark:text-danger-200">
                        <XIcon className="w-3 h-3 me-1" />
                        {meta.declinedLabel}
                    </span>
                )
            default:
                return (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-warn-100 text-warn-800 dark:bg-warn-900 dark:text-warn-200">
                        {meta.pendingLabel}
                    </span>
                )
        }
    }

    const formatDateTime = (dateStr: string) => {
        if (!dateStr) return '-'
        const date = new Date(dateStr)
        return date.toLocaleString('en-GB', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        })
    }

    const formatTime = (timeStr: string) => {
        if (!timeStr) return '-'
        // Handle both "HH:MM:SS" and full datetime strings
        const parts = timeStr.split(':')
        if (parts.length >= 2) {
            return `${parts[0]}:${parts[1]}`
        }
        return timeStr
    }


    return (
        <div className="overflow-x-auto bg-white dark:bg-gray-800 rounded-lg shadow">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-900">
                    <tr>
                        <th scope="col" className="px-4 py-3 text-start">
                            <input
                                type="checkbox"
                                checked={allSelected}
                                ref={(input) => {
                                    if (input) {
                                        input.indeterminate = someSelected
                                    }
                                }}
                                onChange={(e) => onSelectAll(e.target.checked)}
                                className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                            />
                        </th>
                        <th scope="col" className="px-4 py-3 text-start text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                            {meta.tableVolunteerColumn}
                        </th>
                        <th scope="col" className="px-4 py-3 text-start text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                            {meta.tableStatusColumn}
                        </th>
                        <th scope="col" className="px-4 py-3 text-start text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider max-w-xs">
                            {meta.tableDeclineReasonColumn}
                        </th>
                        <th scope="col" className="px-4 py-3 text-start text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                            {meta.tableEventColumn}
                        </th>
                        <th scope="col" className="px-4 py-3 text-start text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                            {meta.tableAreaColumn}
                        </th>
                        <th scope="col" className="px-4 py-3 text-start text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                            {meta.tableShiftColumn}
                        </th>

                        <th scope="col" className="px-4 py-3 text-start text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                            {meta.tableTknColumn}
                        </th>
                        <th scope="col" className="px-4 py-3 text-start text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                            {meta.tableResponseDateColumn}
                        </th>
                        {canReallocate && (
                            <th scope="col" className="px-4 py-3 text-start text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                {meta.tableActionsColumn}
                            </th>
                        )}
                    </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                    {allocations.map((item) => {
                        const volunteer = item.allocation?.volunteer
                        const shift = item.allocation?.shift
                        const area = shift?.area
                        const event = area?.event
                        const duty = shift?.duty

                        return (
                            <tr
                                key={item.id}
                                className={`hover:bg-gray-50 dark:hover:bg-gray-700 ${selectedIds.includes(item.id) ? 'bg-primary-50 dark:bg-primary-900/20' : ''
                                    }`}
                            >
                                <td className="px-4 py-3">
                                    <input
                                        type="checkbox"
                                        checked={selectedIds.includes(item.id)}
                                        onChange={(e) => onSelectOne(item.id, e.target.checked)}
                                        className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                                    />
                                </td>
                                <td className="px-4 py-3 whitespace-nowrap">
                                    <div className="flex flex-col">
                                        <button
                                            type="button"
                                            onClick={(e) => {
                                                e.stopPropagation()
                                                onProfileClick && onProfileClick(item)
                                            }}
                                            className="text-primary-600 hover:text-primary-800 dark:text-primary-400 dark:hover:text-primary-300 hover:underline font-medium inline-flex items-center gap-1"
                                        >
                                            <EyeIcon className="w-4 h-4" />
                                            {volunteer.first_name} {volunteer.last_name}
                                        </button>
                                        <span className="text-xs text-gray-500 dark:text-gray-400">
                                            {volunteer?.email || '-'}
                                        </span>
                                    </div>
                                </td>
                                <td className="px-4 py-3 whitespace-nowrap">
                                    {getStatusBadge(item.status)}
                                </td>
                                <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300 max-w-xs">
                                    {item.decline_reason ? (
                                        <div className="flex flex-col">
                                            <span className="truncate" title={item.decline_reason}>
                                                {item.decline_reason.length > 40
                                                    ? item.decline_reason.substring(0, 40) + '...'
                                                    : item.decline_reason
                                                }
                                            </span>
                                            {item.decline_details && (
                                                <span className="text-xs text-gray-500 truncate" title={item.decline_details}>
                                                    {item.decline_details.length > 30
                                                        ? item.decline_details.substring(0, 30) + '...'
                                                        : item.decline_details
                                                    }
                                                </span>
                                            )}
                                        </div>
                                    ) : (
                                        '-'
                                    )}
                                </td>
                                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300">
                                    {event?.name || '-'}
                                </td>
                                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300">
                                    {area?.name || '-'}
                                </td>
                                <td className="px-4 py-3 whitespace-nowrap">
                                    <div className="flex flex-col">
                                        <span className="text-sm text-gray-900 dark:text-gray-100">
                                            {shift?.title || duty?.name || '-'}
                                        </span>
                                        <span className="text-xs text-gray-500 dark:text-gray-400">
                                            {formatTime(item.start_time)} - {formatTime(item.end_time)}
                                        </span>
                                    </div>
                                </td>

                                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300">
                                    {item.offered_as_tkn ? (
                                        <CheckIcon className="w-5 h-5 text-success-500" />
                                    ) : item.offered_as_tkn === false ? (
                                        <XIcon className="w-5 h-5 text-gray-400" />
                                    ) : (
                                        '-'
                                    )}
                                </td>

                                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300">
                                    {formatDateTime(item.updated_at)}
                                </td>
                                <td>
                                    <div className='ms-2'>
                                        {!item.reviewed_status ? (
                                            <button
                                                onClick={() => { onReviewClick([item.id], true) }}
                                                className="p-2 bg-success-600 text-white rounded hover:bg-success-700 transition-colors"
                                                title="Approve"
                                                type='button'
                                            >
                                                <CheckIcon className="w-4 h-4" />
                                            </button>
                                        ) : (
                                            <button
                                                onClick={() => { onReviewClick([item.id], false) }}
                                                className="p-2 bg-warn-600 text-white rounded hover:bg-warn-700 transition-colors"
                                                title="Unapprove"
                                                type='button'
                                            >
                                                <XIcon className="w-4 h-4" />
                                            </button>
                                        )}
                                    </div>
                                </td>
                                {canReallocate && (
                                    <td className="px-4 py-3 whitespace-nowrap">
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={() => onReAllocate(item.id)}
                                        >
                                            {meta.reAllocateButton}
                                        </Button>
                                    </td>
                                )}
                            </tr>
                        )
                    })}
                </tbody>
            </table>
        </div>
    )
}
